/**
 * Reads the Supabase auth session directly from SSR cookies.
 *
 * The @supabase/ssr client stores the full session JSON (with real tokens)
 * in chunked, base64url-encoded cookies. Its JS API (getSession()) returns
 * short 12-char references instead. Reading cookies is the only way to get
 * the actual refresh token for Keychain storage.
 */

const BASE64_PREFIX = 'base64-';

function base64UrlToString(encoded: string): string {
  const padded = encoded + '==='.slice(0, (4 - (encoded.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64);
}

/**
 * Derive the cookie storage key from the Supabase URL.
 * @supabase/ssr uses `sb-{project-ref}-auth-token` by default.
 */
function getStorageKey(): string {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
  const match = url.match(/https?:\/\/([^.]+)/);
  const ref = match?.[1] ?? 'unknown';
  return `sb-${ref}-auth-token`;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)',
    ),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Read and reconstruct the Supabase session from SSR cookies.
 * Handles both chunked and non-chunked cookies, and base64url encoding.
 */
export function readSessionFromCookies(): {
  access_token: string;
  refresh_token: string;
  user?: { id: string };
} | null {
  const key = getStorageKey();

  // Try non-chunked first
  let raw = getCookie(key);

  if (!raw) {
    // Try chunked: key.0, key.1, key.2, ...
    const chunks: string[] = [];
    for (let i = 0; ; i++) {
      const chunk = getCookie(`${key}.${i}`);
      if (!chunk) break;
      chunks.push(chunk);
    }
    if (chunks.length > 0) {
      raw = chunks.join('');
    }
  }

  if (!raw) return null;

  try {
    let decoded = raw;
    if (decoded.startsWith(BASE64_PREFIX)) {
      decoded = base64UrlToString(decoded.slice(BASE64_PREFIX.length));
    }

    const parsed = JSON.parse(decoded);
    if (parsed?.access_token && parsed?.refresh_token) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
